import { useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { Menu, X } from 'lucide-react';
import { MenuKey, type NavigationItem } from '@tbbt/shared';
import { cn } from '@/lib/cn';
import { FOOTER_COLUMNS } from './footer-columns';
import { useSite } from '@/app/site-context';
import { Button, ButtonLink, Container } from '@/components/ui';
import { BrandMark } from './BrandMark';

/**
 * The public site chrome: skip link, header, page outlet, footer.
 *
 * Navigation is entirely data-driven — every link comes from the API's navigation
 * manifest, which the admin controls. Nothing here hard-codes a menu, which is what
 * makes "hide a page" and "reorder the menu" work without a deploy.
 */
export function PublicLayout(): ReactNode {
  return (
    /*
      `overflow-x-clip` is a safety net, not the fix. One element a few pixels wider than a
      phone makes the whole document wider, and mobile browsers respond by zooming the page
      out to fit it — every section then sits left of centre with a strip of empty space on
      the right, and fixed layers like the loader centre on the wider page. The cause gets
      fixed where it is (see `ContactBlock`); this stops the next one from doing the same.

      `clip` rather than `hidden`: it makes no scroll container, so nothing sticky inside
      breaks, and the fixed header is not clipped by it. Scoped to the public site, so
      admin screens that scroll a wide table sideways are unaffected.
    */
    <div className="flex min-h-dvh flex-col overflow-x-clip">
      {/*
        The first focusable element on the page. Keyboard and screen-reader users can
        jump straight past a header that is otherwise five tab stops deep on every
        single page.
      */}
      <a href="#main" className="skip-link">
        Skip to main content
      </a>

      <SiteHeader />

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <SiteFooter />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Header
// -----------------------------------------------------------------------------

/** Near the top of the page the header always shows: there is nothing under it to uncover. */
const REVEAL_ZONE_PX = 120;

/** Movement smaller than this is ignored, so a trackpad's jitter cannot flicker the header. */
const SCROLL_INTENT_PX = 8;

/** How long a header revealed by hovering lingers after the pointer leaves, before tucking away. */
const HOVER_LINGER_MS = 450;

function SiteHeader(): ReactNode {
  const { navigation, settings } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  /**
   * Whether the header has slid up out of the way.
   *
   * The pill floats over the page, so while reading down it covered whatever was scrolling
   * under it — a card's heading, the first line of a paragraph — and the reader had to nudge
   * the page back and forth to see it. Now it tucks away while the page moves down and
   * comes back the moment the reader does anything that means "I want the menu": scrolling
   * up, pointing at the top edge of the window, or tabbing into it.
   *
   * Refs, not state, for what the handlers read: none of it changes what is painted, and
   * the scroll listener is registered once and would otherwise see stale values.
   */
  const [tucked, setTucked] = useState(false);
  const pointerInside = useRef(false);
  const focusInside = useRef(false);
  const revealedByHover = useRef(false);
  const lingerTimer = useRef<number | undefined>(undefined);

  /**
   * The mobile menu must close on navigation, or it stays open covering the new page.
   *
   * Derived rather than reset in an effect: state is the path the menu was opened on, so a
   * navigation closes it automatically because the pathname no longer matches. An effect
   * calling `setMobileOpen(false)` would render twice on every navigation and is what
   * `react-hooks/set-state-in-effect` warns about.
   */
  const [openedOnPath, setOpenedOnPath] = useState<string | null>(null);
  const mobileOpen = openedOnPath === location.pathname;
  const setMobileOpen = (open: boolean): void => setOpenedOnPath(open ? location.pathname : null);

  /**
   * Tighten the header once the page scrolls, and tuck it away while the page moves down.
   * `passive: true` tells the browser the listener will not call `preventDefault`, so it
   * can keep scrolling on the compositor thread instead of waiting for JavaScript.
   *
   * Direction is measured against the last position that *counted*, not the last event:
   * a slow scroll arrives as many tiny steps, each under the threshold, and comparing
   * event to event would never see it move at all.
   */
  useEffect(() => {
    let lastY = window.scrollY;

    const onScroll = (): void => {
      const y = window.scrollY;
      setScrolled(y > 24);

      if (y < REVEAL_ZONE_PX) {
        lastY = y;
        setTucked(false);
        return;
      }

      const delta = y - lastY;
      if (Math.abs(delta) < SCROLL_INTENT_PX) return;
      lastY = y;

      if (delta < 0) {
        revealedByHover.current = false;
        setTucked(false);
      } else if (!pointerInside.current && !focusInside.current) {
        // Not while the pointer or focus is on it: a menu that slides out from under
        // the cursor that is reaching for it is the worst version of this.
        revealedByHover.current = false;
        setTucked(true);
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => () => window.clearTimeout(lingerTimer.current), []);

  /*
    Pointing at the top of the window brings the header back, and — if that is what
    brought it back — moving away lets it tuck up again after a beat, the way an
    auto-hiding menu bar behaves. A header revealed by scrolling up stays put until the
    next scroll down; that reader asked for it, not merely passed through.
  */
  const onPointerEnter = (): void => {
    pointerInside.current = true;
    window.clearTimeout(lingerTimer.current);
    if (tucked) {
      revealedByHover.current = true;
      setTucked(false);
    }
  };

  const onPointerLeave = (event: PointerEvent<HTMLElement>): void => {
    pointerInside.current = false;
    // Touch has no hover to leave: a tap on the edge reveals it, and a scroll hides it.
    if (event.pointerType !== 'mouse' || !revealedByHover.current) return;

    lingerTimer.current = window.setTimeout(() => {
      if (pointerInside.current || focusInside.current || window.scrollY < REVEAL_ZONE_PX) return;
      revealedByHover.current = false;
      setTucked(true);
    }, HOVER_LINGER_MS);
  };

  // Prevent the page behind the mobile menu from scrolling while it is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  const items = navigation[MenuKey.HEADER];
  const links = items.filter((item) => !item.isCta);
  const cta = items.find((item) => item.isCta);

  // An open mobile menu is never tucked away: the page behind it is locked, so nothing
  // would scroll it back.
  const concealed = tucked && !mobileOpen;

  return (
    /*
      `pointer-events-none` on the header itself, and back on for the pill, the menu and
      the edge strip. The header box is the full width of the window; before, the empty
      space either side of the pill swallowed clicks meant for the page beneath it, and a
      tucked-away header would have left an invisible band doing the same.
    */
    <header
      data-tucked={concealed ? '' : undefined}
      className="pointer-events-none fixed inset-x-0 top-0 z-50 pt-6"
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      // `onFocus`/`onBlur` bind the bubbling `focusin`/`focusout`, so tabbing to any link
      // in the pill reveals it — it is still in the tab order while tucked away.
      onFocus={() => {
        focusInside.current = true;
        setTucked(false);
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) focusInside.current = false;
      }}
    >
      {/*
        The edge that brings a tucked header back. Tall enough to cover the gap above the
        pill, so the pointer can travel from the edge onto the pill without leaving the
        header in between and tucking it straight back up.

        The short bar in it is the only hint the menu is up there. It shows only while the
        header is tucked away, and is decoration: the header is reachable without it.
      */}
      <div aria-hidden="true" className="pointer-events-auto absolute inset-x-0 top-0 h-7">
        <span
          className={cn(
            // The white halo is what makes it read as a control rather than a stray mark
            // when it lands on top of a heading.
            'mx-auto mt-2 block h-1.5 w-12 rounded-full bg-primary/55 transition-opacity duration-300',
            'shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-surface)_85%,transparent),0_2px_8px_rgba(99,14,212,0.25)]',
            concealed ? 'opacity-100 delay-200' : 'opacity-0',
          )}
        />
      </div>

      <Container
        pad="gutter"
        className={cn(
          /*
            The slide. Up past its own height and its drop shadow, so nothing of it is
            left peeking over the page. Translate only, never opacity: the pill is frosted
            glass, and an ancestor below full opacity cuts its `backdrop-filter` off from
            the page it is meant to blur, so a fade flashes it clear mid-animation.

            Away quickly and accelerating, back slower and settling, which is what makes
            the return feel like it arrived rather than snapped in.
          */
          'transition-[translate] motion-reduce:transition-none',
          concealed
            ? 'translate-y-[calc(-100%-3.5rem)] duration-300 ease-in'
            : 'duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
        )}
      >
        <nav
          aria-label="Main navigation"
          className={cn(
            'pointer-events-auto',
            // 900px inside the 1240px container, which is what gives the pill the
            // floating, inset look in the reference design.
            'glass-panel mx-auto w-full max-w-[900px] items-center gap-3 sm:gap-4',
            /*
              Below `lg` a three-column grid — an empty track, the brand, the hamburger — so
              the logo and name sit in the middle of the pill rather than hugging its left
              edge. The two outer tracks are both `1fr`, which is what keeps the brand
              centred on the pill and not merely in the space the button leaves. On a very
              narrow screen the empty track gives way first, so the brand slides left
              before anything overlaps. From `lg` it is the desktop row again.
            */
            'grid grid-cols-[1fr_auto_1fr] lg:flex lg:justify-between',
            // Tighter on a phone, so the wordmark fits beside the hamburger at 320px.
            'rounded-full border-surface-variant px-4 py-3 sm:px-6',
            // `nav-pill` carries the raised edge and the hover glow; see index.css.
            'nav-pill group/nav',
            scrolled ? 'shadow-soft' : 'shadow-card',
          )}
        >
          {/*
            Mark plus wordmark, at every width. The reference design had the mark alone,
            which works for a logo people already recognise and not for one nobody has
            seen yet — and a phone is where most people will meet it first. The wordmark
            used to be hidden below `sm` to keep clear of the hamburger; on a phone the
            mark and the type step down a size instead, which leaves room for both. If a
            very narrow screen still runs short, the name truncates rather than wrapping
            the pill onto two lines or pushing the menu button off the edge.

            `aria-label` stays the accessible name, and the mark and wordmark are both
            hidden from assistive technology — otherwise the link announces the brand
            name twice over.
          */}
          <Link
            to="/"
            // `col-start-2`: the middle track of the grid below `lg`; ignored by the flex row.
            className="col-start-2 flex min-w-0 items-center gap-2 rounded-full sm:gap-2.5"
            aria-label={`${settings.brand.name} — home`}
          >
            <BrandMark className="size-9 sm:size-11" />
            <span
              aria-hidden="true"
              className="truncate text-[0.9rem] font-semibold tracking-tight text-heading sm:text-[0.95rem]"
            >
              {settings.brand.name}
            </span>
          </Link>

          {/*
            The full menu from `lg`, not `md`. Mark, wordmark, six links and the button need
            about 800px; at `md` (768px) the pill has ~650, so the row either wrapped or cut
            the brand name to "The Bl…". Tablets get the hamburger and the whole name.
          */}
          <ul className="hidden items-center gap-1 lg:flex">
            {links.map((item) => (
              <li key={item.id}>
                <NavItemLink item={item} />
              </li>
            ))}
          </ul>

          <div className="col-start-3 flex shrink-0 items-center gap-2 justify-self-end">
            {cta ? (
              <ButtonLink
                to={cta.href}
                size="sm"
                className="hidden h-10 px-6 text-[14px] lg:inline-flex"
              >
                {cta.label}
              </ButtonLink>
            ) : null}

            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              // Both attributes matter: `expanded` conveys state, `controls` ties the
              // button to the panel it toggles.
              aria-expanded={mobileOpen}
              aria-controls="mobile-navigation"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />}
            </Button>
          </div>
        </nav>

        {mobileOpen ? (
          <div
            id="mobile-navigation"
            /*
              `bg-surface/95` over the glass's usual 82%. The pill is a strip and can afford
              to let the page show through; this panel covers most of a phone screen of
              text, and a menu is read, not glanced at. Near-opaque, it stays legible even
              on a browser that does not blur at all.
            */
            className="glass-panel pointer-events-auto mt-2 flex flex-col gap-1 rounded-card bg-surface/95 p-4 lg:hidden"
          >
            {links.map((item) => (
              <NavItemLink key={item.id} item={item} className="w-full px-4 py-3" />
            ))}
            {cta ? (
              <ButtonLink to={cta.href} className="mt-2 w-full">
                {cta.label}
              </ButtonLink>
            ) : null}
          </div>
        ) : null}
      </Container>
    </header>
  );
}

function NavItemLink({ item, className }: { item: NavigationItem; className?: string }): ReactNode {
  // The design's desktop links are plain 14px text that changes colour on hover; the
  // mobile panel adds its own padding on top via `className`.
  /*
    `inline-block` is load-bearing, not cosmetic. An `<a>` is inline by default, and
    `transform` does not apply to a non-replaced inline element — the hover scale below
    would compute to a matrix in devtools and move nothing on screen. The padding goes
    with it: without a box there is nothing for the hover tint to fill but the glyphs.
  */
  const base =
    'nav-link relative inline-block rounded-full px-3 py-1.5 text-[14px] font-medium transition-[color,background-color,box-shadow,transform] duration-200 hover:text-primary';

  if (item.isExternal) {
    return (
      <a
        href={item.href}
        // `noopener` is not optional on a target=_blank link: without it the opened
        // page gets a `window.opener` handle back to ours and can navigate it away.
        {...(item.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={cn(base, 'text-body', className)}
      >
        {item.label}
      </a>
    );
  }

  return (
    <NavLink
      to={item.href}
      // `end` so "/" is only active on the homepage rather than on every route.
      end={item.href === '/'}
      className={({ isActive }) =>
        cn(base, isActive ? 'text-primary-strong' : 'text-body', className)
      }
    >
      {item.label}
    </NavLink>
  );
}

// -----------------------------------------------------------------------------
// Footer
// -----------------------------------------------------------------------------

/** The eyebrow over a footer column. Shared so the Contact column cannot drift from the
 *  three navigation columns beside it. */
const footerHeading = 'text-eyebrow font-bold uppercase tracking-[0.1em] text-white/50';

/** A footer link's colour and hover, with no size of its own so it takes the column's.
 *  The Contact column sets 13px and its links must not jump back up to 14px. */
const footerLinkBase = 'text-white/70 transition-colors hover:text-white';

/** A link in one of the three navigation columns. */
const footerLink = `text-sm ${footerLinkBase}`;

function SiteFooter(): ReactNode {
  const { navigation, settings } = useSite();
  const year = new Date().getFullYear();

  const columns = FOOTER_COLUMNS;

  const primaryOffice =
    settings.contact.offices.find((office) => office.isPrimary) ?? settings.contact.offices[0];

  /*
    The postal address as the lines a reader sees, in reading order: whatever the office
    carries — legal entity, street, PO box — and then the city and country together on a
    line of their own.

    Assembled here rather than in the JSX so the column can tell whether it has anything
    to show. Every field behind it defaults to empty in the settings schema, and a
    "Contact" heading standing over nothing is worse than no column at all.
  */
  const addressLines = primaryOffice
    ? [
        ...primaryOffice.addressLines,
        [primaryOffice.city, primaryOffice.country].filter(Boolean).join(', '),
      ]
        .map((line) => line.trim())
        .filter(Boolean)
    : [];

  const { email, phone } = settings.contact;
  // `tel:` takes digits and a leading `+`; the spaces that make the number readable on
  // the page are not part of the number a dialler can use.
  const phoneHref = phone.replace(/[^\d+]/g, '');

  return (
    /*
      `bg-footer`, not `bg-ink`: the client chose the slate blue out of the ten previewed
      here, and the purple stays on the CTA band and the `ink` tones that also read
      `--color-ink`. The swatch picker that ran that comparison is gone.
    */
    <footer className="bg-footer text-white">
      <Container pad="gutter" className="py-12 md:py-16">
        {/*
          Five columns, but not five equal ones. The brand and the address are given about
          a fifth more than the three link lists, because they carry the footer's widest
          lines — a 160px brand name beside a 44px mark, and a street address — while
          "Executive search" and "About us" need less.

          Five *equal* columns leave each one 219px, against the 214px the brand name and
          mark need together: it fitted by five pixels, which in practice meant it did not,
          and the name wrapped onto a second line. The ratios here leave every column
          20-40px of slack instead, which is why the longest service name also stays on one
          line. `whitespace-nowrap` on the name states the requirement the track is sized
          for, so a sixth column cannot quietly break it again.

          The full row waits for `xl`, since five columns need ~1010px of content width and
          a 1024px viewport offers 976px. Below that it steps down to three columns and
          then two rather than shrinking every column past its content.
        */}
        <div className="mb-10 grid gap-x-6 gap-y-12 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[1.2fr_1fr_1fr_1fr_1.15fr]">
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2.5">
              <BrandMark />
              <span className="whitespace-nowrap text-[1.05rem] font-semibold text-white">
                {settings.brand.name}
              </span>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-white/70">
              {settings.brand.positioning}
            </p>
          </div>

          {columns.map((column) => {
            const items = navigation[column.key];
            // A column whose every item is hidden renders nothing rather than an
            // orphaned heading.
            if (items.length === 0) return null;

            return (
              <nav key={column.key} aria-label={column.heading} className="flex flex-col gap-4">
                <p className={footerHeading}>{column.heading}</p>
                <ul className="flex flex-col gap-3">
                  {items.map((item) => (
                    <li key={item.id}>
                      {item.isExternal ? (
                        <a
                          href={item.href}
                          {...(item.openInNewTab
                            ? { target: '_blank', rel: 'noopener noreferrer' }
                            : {})}
                          className={footerLink}
                        >
                          {item.label}
                        </a>
                      ) : (
                        <Link to={item.href} className={footerLink}>
                          {item.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            );
          })}

          {/*
            The registered address, phone and inbox — the details a visitor needs to reach
            the company off-site, which until now were an email and a bare "Dubai, United
            Arab Emirates" tucked under the brand blurb.

            An `<address>` rather than a `<div>`: this is the contact information for the
            page's owner, which is exactly what the element is for, and it gives a screen
            reader something to announce the block as. `not-italic` because browsers
            italicise it by default and the rest of the footer is upright.
          */}
          {addressLines.length > 0 || email || phone ? (
            <address className="flex flex-col gap-4 not-italic">
              <p className={footerHeading}>Contact</p>
              {/*
                13px rather than the 14px the link lists use. An address is reference
                detail rather than something a visitor scans for, so setting it a step
                down reads as secondary — and it keeps the longest line, "Plot 12,
                Jumeirah Lakes Towers", clear of the track's width by a good margin
                instead of by a few pixels.
              */}
              <div className="flex flex-col gap-3 text-[0.8125rem] leading-relaxed text-white/70">
                {addressLines.length > 0 ? (
                  <p>
                    {addressLines.map((line) => (
                      <span key={line} className="block">
                        {line}
                      </span>
                    ))}
                  </p>
                ) : null}

                {phone ? (
                  <a href={`tel:${phoneHref}`} className={cn('w-fit', footerLinkBase)}>
                    {phone}
                  </a>
                ) : null}

                {/* `w-fit` on both links: a stretched flex child would give the phone a
                    hover target, and the email an underline, the full width of the
                    column rather than the width of the text. */}
                {email ? (
                  <a
                    href={`mailto:${email}`}
                    className="w-fit text-secondary-container underline-offset-4 hover:underline"
                  >
                    {email}
                  </a>
                ) : null}
              </div>
            </address>
          ) : null}
        </div>

        <div className="flex flex-col gap-4 border-t border-surface-variant/20 pt-6 text-sm text-white/50 md:flex-row md:items-center md:justify-between">
          <p>
            © {year} {settings.brand.name}. All rights reserved.
          </p>
          {settings.brand.tagline ? (
            <p className="italic text-white/40">{settings.brand.tagline}</p>
          ) : null}
        </div>
      </Container>
    </footer>
  );
}
